<template>
    <v-dialog
        :value="isDialogOpen"
        max-width="700px"
        transition="slide-y-transition"
        @keydown.esc="handleCloseDialog"
        @click:outside="handleCloseDialog"
    >
        <v-card>
            <v-card-title>
                <h1 class="text-subtitle-1">
                    Wybierz wadliwy element zamówienia
                </h1>
            </v-card-title>

            <v-card-text style="max-height:70vh;overflow-y:auto;">
                <div v-if="!ordersData.length" class="text-center py-6 grey--text">
                    Brak komponentów w wybranym zleceniu.
                </div>

                <div
                    v-for="(order, idx) in ordersData"
                    :key="order.orderNumber"
                    class="mb-6"
                >
                    <div class="d-flex align-center justify-space-between mb-2">
                        <div class="d-flex align-center" style="gap:12px;">
                            <div>
                                <div class="text-caption grey--text text--darken-1">
                                    Numer zamówienia
                                </div>
                                <v-chip small label color="grey lighten-3" class="font-weight-medium">
                                    {{ order.orderNumber }}<span v-if="order.orderDescription"> - {{ order.orderDescription }}</span>
                                </v-chip>
                            </div>
                            <v-checkbox
                                v-model="order.selectAll"
                                hide-details
                                class="ma-0"
                                label="Zaznacz wszystkie komponenty w zamówieniu"
                                @change="toggleSelectAll(order)"
                            />
                        </div>
                    </div>

                    <v-simple-table dense>
                        <thead>
                            <tr>
                                <th style="width:40px;" />
                                <th class="text-left">
                                    Komponenty
                                </th>
                                <th class="text-center" style="width:90px;">
                                    Nakład
                                </th>
                                <th class="text-center" style="width:140px;">
                                    Wadliwy nakład
                                </th>
                                <th class="text-center" style="width:120px;">
                                    Wadliwy nakład w %
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                v-for="component in order.components"
                                :key="component.id"
                                :class="{'grey lighten-4': component.isSignature}"
                            >
                                <td>
                                    <v-checkbox
                                        v-if="!component.isSignature"
                                        v-model="component.selected"
                                        hide-details
                                        class="ma-0 pa-0"
                                        @change="onComponentSelect(order, component)"
                                    />
                                </td>
                                <td>
                                    <div class="d-flex align-center">
                                        <v-icon
                                            v-if="component.signatures && component.signatures.length && !component.isSignature"
                                            small
                                            class="mr-1 cursor-pointer"
                                            color="primary"
                                            @click.stop="openSignaturesDialog(order, component)"
                                        >
                                            mdi-plus
                                        </v-icon>
                                        <span v-if="!component.isSignature">{{ component.name }}</span>
                                        <span v-else class="ml-6">{{ component.name }}</span>
                                    </div>
                                </td>
                                <td class="text-center">
                                    {{ component.amount }}
                                </td>
                                <td class="text-center" style="vertical-align:middle;">
                                    <v-text-field
                                        v-if="!component.isSignature"
                                        v-model.number="component.defectiveAmount"
                                        type="number"
                                        dense
                                        :disabled="!component.selected || component.lockedBySignatures"
                                        :min="0"
                                        :error="isDefectiveGreater(component) || getDefectiveError(component).length > 0"
                                        :error-messages="getDefectiveError(component)"
                                        :hide-details="getDefectiveError(component).length === 0"
                                        style="max-width:110px;"
                                        @input="onDefectiveInput(component)"
                                        @blur="onDefectiveBlur(component)"
                                    />
                                    <v-text-field
                                        v-else
                                        v-model.number="component.defectiveAmount"
                                        type="number"
                                        dense
                                        hide-details
                                        :disabled="true"
                                        style="max-width:110px;"
                                    />
                                </td>
                                <td class="text-center">
                                    <span>
                                        {{ formatPercent(component.defectiveAmountPercent) }}
                                    </span>
                                </td>
                            </tr>
                        </tbody>
                    </v-simple-table>
                    <v-divider
                        v-if="idx < ordersData.length - 1"
                        class="mt-4"
                    />
                </div>
            </v-card-text>

            <v-divider />

            <v-card-actions>
                <v-btn
                    color="error"
                    text
                    @click="handleCloseDialog"
                >
                    Anuluj
                </v-btn>

                <v-spacer />

                <v-btn
                    color="success"
                    text
                    :disabled="saveDisabled"
                    @click="handleSaveDefects"
                >
                    Zapisz
                </v-btn>
            </v-card-actions>
        </v-card>

        <select-signatures-dialog
            :value="signatureDialog.open"
            :signatures="signatureDialog.signatures"
            :component-name="signatureDialog.component && signatureDialog.component.name"
            @on-close="handleSignatureDialogClose"
            @on-save="handleSignatureDialogSave"
        />
    </v-dialog>
</template>

<script>
/* eslint-disable vue/no-mutating-props */
import SelectSignaturesDialog from './SelectSignaturesDialog.vue'

export default {
    name: 'AddOrderDefectDialog',

    components: { SelectSignaturesDialog },

    props: {
        isDialogOpen: {
            type: Boolean
        },
        reportItem: {
            type: Object,
            default: null
        },
        complaint: {
            type: Object,
            default: null
        },
        selectedOrderNumbers: {
            type: Array,
            default: () => []
        }
    },

    data() {
        return {
            ordersData: [],
            signatureDialog: {
                open: false,
                order: null,
                component: null,
                signatures: []
            }
        }
    },

    computed: {
        saveDisabled() {
            return this.ordersData.some(order => order.components.some(c => Number(c.defectiveAmount) > c.amount))
        }
    },

    watch: {
        isDialogOpen(val) {
            if (val) this.initializeOrders()
        }
    },

    methods: {
        formatPercent(value) {
            if (value === null || value === undefined || value === '') return ''

            const num = Number(value)

            if (Number.isNaN(num)) return '%'

            return `${num.toFixed(1)}%`
        },

        isDefectiveGreater(component) {
            if (component.defectiveAmount === '' || component.defectiveAmount === null) return false

            return Number(component.defectiveAmount) > component.amount
        },

        getDefectiveError(component) {
            if (component.defectiveAmount === '' || component.defectiveAmount === null) return []
            const val = Number(component.defectiveAmount)

            if (val < 0 || val > component.amount) {
                return ['Podaj wartość w zakresie 0 - ' + component.amount]
            }

            return []
        },

        parseParentFromComponent(name) {
            if (!name || typeof name !== 'string') return null
            const idx = name.indexOf(',')

            if (idx === -1) return null

            return name.slice(0, idx).trim()
        },

        initializeOrders() {
            const selected = Array.isArray(this.selectedOrderNumbers) ? this.selectedOrderNumbers : []
            const components = (this.reportItem?.itemPlatesOrdersComponents || []).filter(component => selected.includes(component.orderNumber))

            const grouped = {}
            const existingDefects = Array.isArray(this.complaint?.defects) ? this.complaint.defects : []

            components.forEach(component => {
                if (!grouped[component.orderNumber]) grouped[component.orderNumber] = {
                    orderNumber: component.orderNumber,
                    orderDescription: component.orderDescription || component.description || null,
                    components: [],
                    selectAll: false
                }

                const defect = existingDefects.find(defect => defect.orderNumber === component.orderNumber && defect.component === component.name && !defect.isSignature)
                let defectiveAmount = 0
                let defectiveAmountPercent = null
                let selectedFlag = false

                if (defect) {
                    defectiveAmount = Number(defect.defectiveAmount) || 0
                    if (defectiveAmount > 0) {
                        const max = Number(component.amount) || 0
                        const percent = defect.defectiveAmountPercent !== undefined && defect.defectiveAmountPercent !== null
                            ? defect.defectiveAmountPercent
                            : (max ? (defectiveAmount / max) * 100 : 0)
                            
                        defectiveAmountPercent = defectiveAmount > 0 ? Number(percent).toFixed(1) : null
                        selectedFlag = true
                    }
                }
                const compObj = {
                    id: component.id || `${component.orderNumber}-${component.name}`,
                    orderNumber: component.orderNumber,
                    name: component.name,
                    amount: component.amount,
                    selected: selectedFlag,
                    defectiveAmount: defectiveAmount,
                    defectiveAmountPercent: defectiveAmountPercent,
                    signatures: component.signatures || [],
                    lockedBySignatures: false
                }

                grouped[component.orderNumber].components.push(compObj)

                const signatureDefects = existingDefects.filter(d => {
                    if (d.orderNumber !== component.orderNumber) return false
                    const looksLikeSignature = d.isSignature || (/Składka\s+\d+/i.test(d.component) && d.component.includes(','))

                    if (!looksLikeSignature) return false

                    const directParentMatch = d.parentComponent === component.name
                    const parsedParent = this.parseParentFromComponent(d.component)

                    return directParentMatch || parsedParent === component.name
                })

                if (signatureDefects.length) {
                    this.applySignatureDefectsToComponent(compObj, grouped[component.orderNumber], signatureDefects)
                }
            })

            Object.values(grouped).forEach(order => {
                order.selectAll = order.components.length > 0 && order.components.every(component => component.selected)
            })
            this.ordersData = Object.values(grouped)
        },

        toggleSelectAll(order) {
            order.components.forEach(component => {
                component.selected = order.selectAll
                if (!order.selectAll) {
                    component.defectiveAmount = 0
                    component.defectiveAmountPercent = null
                    if (component.lockedBySignatures) component.lockedBySignatures = false
                }
            })
            if (!order.selectAll) {
                order.components = order.components.filter(c => !c.isSignature)
            }
        },
        
        onComponentSelect(order, component) {
            if (!component.selected) {
                component.defectiveAmount = 0
                component.defectiveAmountPercent = null
                if (component.lockedBySignatures) {
                    order.components = order.components.filter(c => c.parentComponentId !== component.id)
                    component.lockedBySignatures = false
                }
                order.selectAll = false
            } else if (order.components.filter(c => !c.isSignature).every(c => c.selected)) {
                order.selectAll = true
            }
        },

        onDefectiveInput(component) {
            if (!component.selected) return
            if (component.defectiveAmount === '' || component.defectiveAmount === null || Number.isNaN(Number(component.defectiveAmount))) {
                component.defectiveAmount = 0
                component.defectiveAmountPercent = null

                return
            }
            if (component.defectiveAmount < 0) component.defectiveAmount = 0

            if (Number(component.defectiveAmount) > component.amount) {
                component.defectiveAmountPercent = null
                component.selected = component.selected || component.defectiveAmount > 0
                const parent = this.ordersData.find(o => o.orderNumber === component.orderNumber)

                if (parent) parent.selectAll = parent.components.filter(c => !c.isSignature).every(c => c.selected)

                return
            }
            const max = Number(component.amount) || 0
            const percent = max ? (component.defectiveAmount / max) * 100 : 0

            component.defectiveAmountPercent = component.defectiveAmount > 0 ? percent.toFixed(1) : null
            component.selected = component.selected || component.defectiveAmount > 0
            const parent = this.ordersData.find(o => o.orderNumber === component.orderNumber)

            if (parent) parent.selectAll = parent.components.filter(c => !c.isSignature).every(c => c.selected)
        },

        onDefectiveBlur(component) {
            if (!component.selected) return
            if (!component.defectiveAmount || component.defectiveAmount <= 0) {
                component.defectiveAmount = 0
                component.defectiveAmountPercent = null
            }
        },

        openSignaturesDialog(order, component) {
            this.signatureDialog.order = order
            this.signatureDialog.component = component

            const existingSignatureRows = order.components.filter(c => c.isSignature && c.parentComponentId === component.id)
            const signaturesBase = (component.signatures || []).map(sig => ({
                signatureNumber: sig.signatureNumber,
                signatureAmount: sig.signatureAmount,
                selected: false,
                defectiveAmount: 0,
                defectiveAmountPercent: null
            }))

            if (existingSignatureRows.length) {

                existingSignatureRows.forEach(row => {
                    const numMatch = /Składka\s+(\d+)/i.exec(row.name)
                    const signatureNumber = numMatch ? Number(numMatch[1]) : null
                    const target = signaturesBase.find(s => s.signatureNumber === signatureNumber)

                    if (target) {
                        target.selected = true
                        target.defectiveAmount = row.defectiveAmount
                        target.defectiveAmountPercent = row.defectiveAmountPercent
                    }
                })
            }
            this.signatureDialog.signatures = signaturesBase
            this.signatureDialog.open = true
        },

        handleSignatureDialogClose() {
            this.signatureDialog.open = false
        },

        handleSignatureDialogSave(selectedSignatures) {
            const { order, component } = this.signatureDialog

            if (!order || !component) {
                this.signatureDialog.open = false

                return
            }

            order.components = order.components.filter(c => c.parentComponentId !== component.id)
            if (selectedSignatures.length) {
                const signatureRows = selectedSignatures.map(s => ({
                    id: `${component.id}-signature-${s.signatureNumber}`,
                    orderNumber: order.orderNumber,
                    name: `Składka ${s.signatureNumber}`,
                    amount: s.signatureAmount,
                    selected: true,
                    defectiveAmount: s.defectiveAmount,
                    defectiveAmountPercent: s.defectiveAmountPercent,
                    isSignature: true,
                    parentComponentId: component.id,
                    parentComponent: component.name
                }))
                const idx = order.components.findIndex(c => c.id === component.id)

                order.components.splice(idx + 1, 0, ...signatureRows)

                component.defectiveAmount = 0
                component.defectiveAmountPercent = null
                component.selected = true
                component.lockedBySignatures = true
            } else {
                component.lockedBySignatures = false
                component.defectiveAmount = 0
                component.defectiveAmountPercent = null
                component.selected = false
            }
            order.selectAll = order.components.every(c => !c.isSignature && c.selected)
            this.signatureDialog.open = false
        },

        applySignatureDefectsToComponent(component, orderGroup, signatureDefects) {
            const signatureRows = signatureDefects.map(d => {
                const match = /Składka\s+(\d+)/i.exec(d.component)
                const displayName = match ? `Składka ${match[1]}` : d.component
                const signatureNumber = match ? match[1] : (d.signatureNumber || displayName)

                return {
                    id: `${component.id}-signature-${signatureNumber}`,
                    orderNumber: d.orderNumber,
                    name: displayName,
                    amount: d.amount,
                    selected: true,
                    defectiveAmount: d.defectiveAmount,
                    defectiveAmountPercent: d.defectiveAmountPercent,
                    isSignature: true,
                    parentComponentId: component.id,
                    parentComponent: component.name
                }
            })
            
            if (signatureRows.length) {
                const idx = orderGroup.components.findIndex(c => c.id === component.id)

                orderGroup.components.splice(idx + 1, 0, ...signatureRows)
                component.defectiveAmount = 0
                component.defectiveAmountPercent = null
                component.selected = true
                component.lockedBySignatures = true
            }
        },

        handleCloseDialog() { 
            this.$emit('on-close') 
        },

        handleSaveDefects() {
            if (!Array.isArray(this.complaint.defects)) this.$set(this.complaint, 'defects', [])
            const orderNumbers = this.ordersData.map(o => o.orderNumber)

            this.complaint.defects = this.complaint.defects.filter(defect => {
                const ord = defect.orderNumber

                return !orderNumbers.includes(ord)
            })

            const jobNumber = this.reportItem?.primaryNumber || this.reportItem?.jobNumber || this.complaint?.jobNumber || ''
            
            this.ordersData.forEach(order => {
                order.components
                    .filter(component => component.selected && component.defectiveAmount > 0 && component.defectiveAmount <= component.amount && !component.isSignature && !component.lockedBySignatures)
                    .forEach(component => {
                        this.complaint.defects.push({
                            jobNumber: jobNumber,
                            orderNumber: order.orderNumber,
                            plateNumber: null,
                            component: component.name,
                            amount: component.amount,
                            defectiveAmount: component.defectiveAmount,
                            defectiveAmountPercent: component.defectiveAmountPercent,
                            orderComponent: true,
                            lockedBySignatures: component.lockedBySignatures
                        })
                    })
                order.components
                    .filter(component => component.isSignature && component.defectiveAmount > 0 && component.defectiveAmount <= component.amount)
                    .forEach(component => {
                        this.complaint.defects.push({
                            jobNumber: jobNumber,
                            orderNumber: order.orderNumber,
                            plateNumber: null,
                            component: `${component.parentComponent}, ${component.name}`,
                            amount: component.amount,
                            defectiveAmount: component.defectiveAmount,
                            defectiveAmountPercent: component.defectiveAmountPercent,
                            orderComponent: true,
                            isSignature: true,
                            parentComponent: component.parentComponent
                        })
                    })
            })
            this.handleCloseDialog()
        },
    }
}
</script>
